import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button, MonoLabel, pushLayer } from '@/design-system';
import { cn } from '@/shared/lib';

import { tourCopy } from './copy';
import { placeBalloon, type Position, type Rect } from './placement';
import { TOUR_ANCHORS, type TourStep } from './tour-steps';

/** De lucht rond het aangewezen element, zodat de rand het niet raakt. */
const SPOTLIGHT_PADDING = 6;

export interface TourOverlayProps {
    steps: readonly TourStep[];
    index: number;
    onBack: () => void;
    onNext: () => void;
    /** Afgemaakt, overgeslagen of met Escape weggezet: voor de API is dat hetzelfde. */
    onClose: () => void;
}

interface Located {
    rect: Rect | null;
    /** Wijst de stap naar de knop Menu omdat de rail is ingeklapt? */
    viaMenu: boolean;
    position: Position | null;
}

/**
 * De rondleiding zelf: een spotlight op één plek en een tekstballon ernaast.
 *
 * - **Waar.** Elke stap zoekt zijn `data-tour`-anker. Staat dat in de rail en is
 *   de rail ingeklapt (smal scherm), dan wijst de stap naar de knop Menu en zegt
 *   de ballon dat je het daar vindt. Is er geen anker in beeld, dan staat de
 *   ballon als kaart in het midden. Meten gebeurt opnieuw bij draaien en schuiven.
 * - **Toegankelijk.** De ballon is een modale dialoog met de stap als naam en de
 *   uitleg als beschrijving; de focus gaat er bij elke stap naartoe en na afloop
 *   terug naar waar hij was. Escape slaat over. De rest van de pagina is
 *   `inert` zolang de rondleiding loopt (`pushLayer`, net als een Drawer).
 * - **Rustig.** Alleen een korte fade van de ballon, en die valt weg bij
 *   "minder beweging". De sync-indicator blijft het enige dat beweegt.
 */
export function TourOverlay({ steps, index, onBack, onNext, onClose }: TourOverlayProps) {
    const step = steps[index];
    const layerRef = useRef<HTMLDivElement | null>(null);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const [located, setLocated] = useState<Located>({ rect: null, viaMenu: false, position: null });
    const counterId = useId();
    const titleId = useId();
    const bodyId = useId();

    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    /* Eén laag boven alles: Escape slaat over, de pagina eronder is inert. */
    useEffect(() => {
        const restoreTo =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const release = pushLayer({
            modal: true,
            element: layerRef.current,
            onEscape: () => onCloseRef.current(),
        });

        return () => {
            release();

            if (restoreTo?.isConnected) {
                restoreTo.focus({ preventScroll: true });
            }
        };
    }, []);

    /* Meten na het tekenen, en opnieuw als het venster of de ballon verandert. */
    useEffect(() => {
        if (!step) {
            return;
        }

        let frame = 0;
        let scrolled = false;

        const measure = () => {
            const { element, viaMenu } = locate(step);

            if (element && !scrolled) {
                scrolled = true;
                element.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
            }

            const rect = element ? toRect(element.getBoundingClientRect()) : null;
            const dialog = dialogRef.current;

            setLocated({
                rect,
                viaMenu,
                position: placeBalloon(
                    rect && padded(rect),
                    { width: dialog?.offsetWidth ?? 0, height: dialog?.offsetHeight ?? 0 },
                    { width: window.innerWidth, height: window.innerHeight },
                ),
            });
        };
        const schedule = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(measure);
        };

        schedule();
        window.addEventListener('resize', schedule);
        window.addEventListener('scroll', schedule, true);

        const observer =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);

        if (dialogRef.current) {
            observer?.observe(dialogRef.current);
        }

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', schedule);
            window.removeEventListener('scroll', schedule, true);
            observer?.disconnect();
        };
    }, [step]);

    /* Elke stap: de focus naar de ballon, zodat een schermlezer hem voorleest. */
    useEffect(() => {
        dialogRef.current?.focus({ preventScroll: true });
    }, [index]);

    if (!step) {
        return null;
    }

    const last = index === steps.length - 1;
    const spotlight = located.rect ? padded(located.rect) : null;

    return createPortal(
        <div ref={layerRef} data-tour-layer="" className="fixed inset-0 z-[70]">
            {spotlight ? (
                <div
                    aria-hidden="true"
                    className="pointer-events-none fixed rounded-[var(--radius-control)] shadow-[0_0_0_9999px_var(--overlay-scrim)] outline-2 outline-[var(--accent)]"
                    style={{
                        top: spotlight.top,
                        left: spotlight.left,
                        width: spotlight.width,
                        height: spotlight.height,
                    }}
                />
            ) : (
                <div aria-hidden="true" className="fixed inset-0 bg-[var(--overlay-scrim)]" />
            )}

            <div
                key={step.key}
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${counterId} ${titleId}`}
                aria-describedby={bodyId}
                tabIndex={-1}
                data-tour-step={step.key}
                className={cn(
                    'fixed flex w-[min(340px,calc(100vw-32px))] flex-col gap-[10px] p-[18px] outline-none',
                    'rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-popover)]',
                    'transition-opacity duration-150 starting:opacity-0',
                    !located.position && 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                )}
                style={located.position ?? undefined}
            >
                <MonoLabel id={counterId} size={10} as="div" className="tracking-[0.1em]">
                    {tourCopy.counter(index + 1, steps.length)}
                </MonoLabel>

                <h2
                    id={titleId}
                    className="m-0 font-[family-name:var(--font-display)] text-[17px] leading-[1.25] font-semibold text-[var(--text-primary)]"
                >
                    {step.title}
                </h2>

                <div id={bodyId} className="text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                    <p className="m-0">{step.body}</p>
                    {located.viaMenu ? <p className="m-0 mt-[6px]">{tourCopy.inMenu}</p> : null}
                </div>

                <div className="mt-[6px] flex flex-wrap items-center gap-2">
                    <Button variant="ghost" aria-label={tourCopy.skipLabel} onClick={onClose}>
                        {tourCopy.skip}
                    </Button>

                    <span className="ml-auto flex gap-2">
                        {index > 0 ? (
                            <Button variant="outline" size="xs" onClick={onBack}>
                                {tourCopy.back}
                            </Button>
                        ) : null}
                        <Button variant="primary" size="xs" onClick={last ? onClose : onNext}>
                            {last ? tourCopy.done : tourCopy.next}
                        </Button>
                    </span>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/** Het eerste anker van deze stap dat echt in beeld staat. */
function visibleAnchor(name: string): HTMLElement | null {
    for (const element of Array.from(
        document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`),
    )) {
        const rect = element.getBoundingClientRect();

        if (rect.width > 0 && rect.height > 0) {
            return element;
        }
    }

    return null;
}

function locate(step: TourStep): { element: HTMLElement | null; viaMenu: boolean } {
    const own = visibleAnchor(step.anchor);

    if (own) {
        return { element: own, viaMenu: false };
    }

    const menu = step.inRail ? visibleAnchor(TOUR_ANCHORS.menu) : null;

    return { element: menu, viaMenu: menu !== null };
}

function toRect(rect: DOMRect): Rect {
    return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

function padded(rect: Rect): Rect {
    return {
        top: rect.top - SPOTLIGHT_PADDING,
        left: rect.left - SPOTLIGHT_PADDING,
        width: rect.width + SPOTLIGHT_PADDING * 2,
        height: rect.height + SPOTLIGHT_PADDING * 2,
    };
}
